import React, { useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { FieldPath, UseFormReturn } from 'react-hook-form'

import useText from '../../hooks/useText'
import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'
import { getProductName } from '../../services/getProductName'
import { IOcppFormInput, useOcppUserCurrentValidation } from '../../hooks/useOcppFormValidation'
import { ICapiFormInput, useCapiUserCurrentValidation } from '../../hooks/useCapiFormValidation'
import { ChargePoint, Connector, useChargePointUpdateChargePointUserSettingsMutation } from '../../redux/api/web-api'
import { Input } from '../../uikit/Input'
import { Dialog } from '../../uikit/Dialog'
import { useToast } from '../../uikit/ToastProvider'
import { isChargingConnector } from '../../../lib/findChargingConnector'
import isOnline from '../../../lib/isOnline'

const formId = 'edit-charge-point-form'

type EditableConnector = Connector & { connectorId: 1 | 2 }

export type EditChargePointDialogProps = {
    chargePoint: ChargePoint | null | undefined
    visible: boolean
    onHide: () => void
    onSaveSettled?: () => void
    editChargerName?: boolean
}

type EditChargePointDialogContentProps = Omit<EditChargePointDialogProps, 'chargePoint'> & {
    chargePoint: ChargePoint
}

export function EditChargePointDialog(props: EditChargePointDialogProps): React.ReactElement | null {
    const { chargePoint } = props

    if (!chargePoint) return null

    const DialogContent = chargePoint.isOcpp ? OcppEditChargePointDialogContent : CapiEditChargePointDialogContent

    return <DialogContent key={chargePoint.id} {...props} chargePoint={chargePoint} />
}

type EditChargePointFormValues = IOcppFormInput | ICapiFormInput

function OcppEditChargePointDialogContent(props: EditChargePointDialogContentProps): React.ReactElement {
    const validationMethods = useOcppUserCurrentValidation(props.chargePoint)

    return <EditChargePointForm {...props} validationMethods={validationMethods} />
}

function CapiEditChargePointDialogContent(props: EditChargePointDialogContentProps): React.ReactElement {
    const validationMethods = useCapiUserCurrentValidation(props.chargePoint)

    return <EditChargePointForm {...props} validationMethods={validationMethods} />
}

function EditChargePointForm<TFormValues extends EditChargePointFormValues>({
    chargePoint,
    visible,
    onHide,
    onSaveSettled,
    editChargerName,
    validationMethods,
}: EditChargePointDialogContentProps & {
    validationMethods: UseFormReturn<TFormValues>
}): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    const [edit, setEdit] = useState(false)
    const availableConnectors = (chargePoint.connectors ?? [])
        .filter(
            (connector: Connector): connector is EditableConnector =>
                isChargingConnector(connector) && (connector.connectorId === 1 || connector.connectorId === 2)
        )
        .sort((a, b) => a.connectorId - b.connectorId)

    const {
        register,
        handleSubmit,
        reset,
        formState: { isValid, isDirty, isSubmitted },
    } = validationMethods

    const title = getProductName(chargePoint.productId)
    const readOnlyReason = chargePoint.ownerReadOnly ? text('msgNoPermissionToEditCharger') : undefined

    const [updateChargePointUserSettings, { isLoading: isSaving }] =
        useChargePointUpdateChargePointUserSettingsMutation()

    useEffect(() => {
        if (visible) {
            reset()
            setEdit(false)
        }
    }, [reset, visible])

    const saveChargePoint = async (formValues: TFormValues) => {
        try {
            await updateChargePointUserSettings({
                chargePointId: chargePoint.id,
                name: formValues.name ?? '',
                userCurrentConnector1: formValues.uc_1,
                userCurrentConnector2: formValues.uc_2,
            }).unwrap()

            toast.notifySuccess(text('msgChargerUpdated'))
        } catch {
            toast.notifyError(text('msgChargePointNotUpdated'))
        } finally {
            onHide()
            onSaveSettled?.()
        }
    }

    const saveDisabled =
        !isDirty || (isSubmitted && !isValid) || !isOnline(chargePoint) || chargePoint.ownerReadOnly || isSaving

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} />
            <Button label={text('btnSave')} type="submit" form={formId} disabled={saveDisabled} loading={isSaving} />
        </div>
    )

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={text('lblChargerInfoDialogTitle')}
            footer={footer}
            data-testid="edit-charge-point-dialog"
        >
            <form id={formId} className="flex flex-column gap-4" onSubmit={handleSubmit(saveChargePoint)} noValidate>
                <section className="flex flex-column gap-3">
                    {edit ? (
                        <Input
                            type="text"
                            id="name"
                            label={text('lblName')}
                            data-testid="name-input"
                            required
                            {...register('name' as FieldPath<TFormValues>)}
                            error={validationMethods.getFieldState('name' as FieldPath<TFormValues>).error?.message}
                        />
                    ) : (
                        <div className="flex align-items-center gap-2">
                            <span className="text-xl font-medium">{chargePoint.name}</span>
                            {editChargerName && (
                                <Button
                                    type="button"
                                    icon="pi pi-pencil"
                                    text
                                    rounded
                                    size="small"
                                    severity="info"
                                    disabled={chargePoint.ownerReadOnly}
                                    tooltip={readOnlyReason}
                                    tooltipOptions={{ position: 'top', showOnDisabled: true }}
                                    aria-label={text('lblEditChargerName')}
                                    onClick={() => setEdit(true)}
                                    data-testid="toggle-name-edit"
                                />
                            )}
                        </div>
                    )}
                    <div
                        className="flex align-items-center gap-2 text-color-secondary"
                        data-testid={`chargePointType-${chargePoint.id}`}
                    >
                        <span>{title}</span>
                        <span>•</span>
                        <span>{chargePoint.isOcpp ? text('lblOcpp') : text('lblCapiField')}</span>
                    </div>
                </section>

                <section className="flex flex-column gap-3">
                    {availableConnectors.map((connector) => {
                        const fieldName = `uc_${connector.connectorId}` as FieldPath<TFormValues>

                        return (
                            <Input
                                key={connector.connectorId}
                                type="number"
                                id={fieldName}
                                label={text('lblCurrentLimitWithMaxVoltage', {
                                    connectorName: adaptConnectorNaming(connector.name),
                                    installationCurrent: connector.installationCurrent,
                                })}
                                data-testid={`userCurrent-input-${connector.connectorId}`}
                                error={validationMethods.getFieldState(fieldName).error?.message}
                                {...register(fieldName)}
                                disabled={
                                    isSaving ||
                                    chargePoint.ownerReadOnly ||
                                    (chargePoint.isOcpp && chargePoint.chargePointStatus !== 'Online')
                                }
                                disabledReason={readOnlyReason}
                            />
                        )
                    })}
                </section>
            </form>
        </Dialog>
    )
}
